import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navItems, brandInfo } from '@/mocks/home';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleNavClick = useCallback(() => {
    window.scrollTo(0, 0);
    setMobileOpen(false);
  }, []);

  const handleLogoClick = useCallback(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-primary-950/15 backdrop-blur-[18px] border-b border-primary-400/15 py-1.5'
          : 'bg-transparent py-3.5'
      }`}
    >
      {/* Subtle blue glow behind scrolled navbar */}
      {scrolled && (
        <div className="absolute inset-0 bg-gradient-to-r from-primary-800/15 via-accent-500/5 to-primary-800/15 pointer-events-none"></div>
      )}

      <div className="relative px-4 md:px-6 lg:px-10">
        <div
          className={`flex items-center justify-between transition-all duration-300 ${
            scrolled ? 'h-12' : 'h-16 md:h-18'
          }`}
        >
          <Link
            to="/"
            onClick={handleLogoClick}
            className={`font-heading font-bold tracking-wide whitespace-nowrap transition-all duration-300 cursor-pointer ${
              scrolled
                ? 'text-base md:text-lg text-white/95'
                : 'text-xl md:text-2xl text-white'
            }`}
            style={{ textShadow: scrolled ? '0 0 20px rgba(100,180,220,0.25)' : '0 2px 12px rgba(0,0,0,0.5)' }}
          >
            <span className={scrolled ? 'text-accent-500' : 'text-accent-300'}>策</span><span className="text-white/95">點文化</span>
          </Link>

          <div className="hidden md:flex items-center gap-1 lg:gap-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={handleNavClick}
                  className={`relative rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    scrolled
                      ? `px-2.5 py-1.5 text-xs font-medium ${isActive ? 'text-accent-500 bg-primary-500/15' : 'text-white/80 hover:text-white hover:bg-white/10'}`
                      : `px-3 py-2 text-sm font-medium ${isActive ? 'text-accent-300 bg-white/10' : 'text-white/85 hover:text-white hover:bg-white/10'}`
                  }`}
                >
                  <LocalizedNavLabel href={item.href} fallback={item.label} />
                  {isActive && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-accent-500"></span>
                  )}
                </Link>
              );
            })}
          </div>

          <LanguageSwitcher />

          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              scrolled ? 'text-white/90' : 'text-white'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-primary-950/15 backdrop-blur-xl border-b border-primary-400/15">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={handleNavClick}
                  className={`px-3 py-2.5 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                    isActive
                      ? 'text-accent-500 bg-primary-500/15'
                      : 'text-white/85 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <LocalizedNavLabel href={item.href} fallback={item.label} />
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
}